/*!
 * liveHelperLink.js — clock(link): Ableton Link through the helper on this computer
 * © 2026 Rob Canning — GPLv3
 *
 * Oscilla Live on a web host has no Oscilla server to reach Link through.
 * The performer runs the helper (server/linkHelper.js, olive-link.mjs)
 * beside Carabiner, and clock(link) joins it on ws://127.0.0.1:17001.
 * Asked for, never tried on its own: a page reaching into its visitor's
 * computer should be the visitor's choice (the browser may ask too). The
 * choice is kept in this browser, so a reload joins again.
 *
 * The helper's clock is not the grid's: alone, the grid counts this
 * machine's clock; in a room, the relay's. So the helper's offset is
 * measured here, NTP-style as serverClock.js does, and every timeline is
 * carried across: Link's into the grid's clock, a change from here into the
 * helper's.
 *
 *   - alone: Link's beat is the grid's; this console's tempo() and
 *     downbeat() go to Link (useClockLink).
 *   - in a room: this player is the room's Link host. Link's beat goes to
 *     the room (shareTimeline), so everyone follows Link; a change in the
 *     room -- anyone's tempo() or downbeat() -- goes to Link. One Link host
 *     a room: two would each push their own.
 */

import { gridNow, setTimeline } from "../cues/audio/beatGrid.js";
import { useClockLink, shareTimeline, onRoomTimeline } from "../cues/audio/roomBeat.js";
import { toLocal } from "../system/serverClock.js";

export const HELPER = "ws://127.0.0.1:17001";
const KEY = "oscilla.linkHelper";
const RETRY_FIRST_MS = 1000;
const RETRY_MOST_MS = 15000;

/** The helper's offset from this machine's clock: the median of the three quickest of the last ten pings. */
export function createOffset(keep = 10) {
  const samples = [];
  let offset = null;
  return {
    sample(t0, t1, theirs) {
      const rtt = t1 - t0;
      if (!(rtt >= 0) || !Number.isFinite(theirs)) return;
      samples.push({ rtt, off: theirs - (t0 + t1) / 2 });
      if (samples.length > keep) samples.shift();
      const best = [...samples].sort((a, b) => a.rtt - b.rtt).slice(0, 3).map(s => s.off).sort((a, b) => a - b);
      offset = best[Math.floor(best.length / 2)];
    },
    get value() { return offset; },
  };
}

/** Helper time -> grid time, and back, through this machine's clock. */
export function converters(offset, grid = { now: gridNow, toLocal }) {
  const toGrid = helperMs => grid.now(helperMs - offset.value).ms;
  const fromGrid = gridMs => {
    const local = grid.now().domain === "server" ? grid.toLocal(gridMs) : gridMs;
    return local == null ? null : local + offset.value;
  };
  return { toGrid, fromGrid };
}

let current = null;            // the one helper link, while on

/** On: the helper joined (and kept, for a reload). Says how it goes through log. */
export function startHelperLink({ address = HELPER, log = msg => console.info(msg) } = {}) {
  if (current) return current;
  try { localStorage.setItem(KEY, address); } catch { /* private mode: this page only */ }
  const offset = createOffset();
  const { toGrid, fromGrid } = converters(offset);
  const inRoom = () => window.socket?.readyState === WebSocket.OPEN;
  const sharedFromLink = new Set();   // "beat|bpm" of Link's beats given to the room: their echo is not sent back
  let ws = null, timer = null, pingTimer = null, stopped = false;
  let retryMs = RETRY_FIRST_MS, saidMissing = false, lastBpm = null;

  const send = m => { if (ws?.readyState === WebSocket.OPEN) ws.send(JSON.stringify(m)); };

  /** Link's beat, from the helper: the grid's, and the room's when in one. */
  function linkBeat(d) {
    if (offset.value == null) return;
    const tl = { ms: toGrid(Number(d.ms)), beat: Number(d.beat), bpm: Number(d.bpm) };
    if (!setTimeline(tl, "link")) return;
    if (lastBpm == null || Math.abs(tl.bpm - lastBpm) > 0.05) log(`clock: Ableton Link, ${+tl.bpm.toFixed(2)} bpm, ${d.peers ?? 0} peer${d.peers === 1 ? "" : "s"}`);
    lastBpm = tl.bpm;
    if (inRoom() && shareTimeline(tl)) {
      sharedFromLink.add(`${tl.beat}|${tl.bpm}`);
      if (sharedFromLink.size > 20) sharedFromLink.delete(sharedFromLink.values().next().value);
    }
  }

  // a change in the room -- anyone's, this console's own too -- goes to Link
  const unwatch = onRoomTimeline((tl, d) => {
    if (sharedFromLink.has(`${d.beat}|${d.bpm}`)) return;     // Link's own beat, come back
    const ms = fromGrid(tl.ms);
    if (ms != null) send({ type: "livecode_timeline_set", ms, beat: tl.beat, bpm: tl.bpm });
  });

  function ask() { send({ type: "livecode_timeline_request" }); }

  function connect() {
    if (stopped) return;
    let me;
    try { me = new WebSocket(address); } catch (err) { log(`clock: the Link helper's address ${address} -- ${err.message}`); return; }
    ws = me;
    me.addEventListener("open", () => me.send(JSON.stringify({ type: "clock_join" })));
    me.addEventListener("message", (e) => {
      let d;
      try { d = JSON.parse(e.data); } catch { return; }
      if (d?.type === "clock_joined") {
        retryMs = RETRY_FIRST_MS; saidMissing = false;
        log(`clock: joined the Link helper${d.link ? "" : " -- it has not found Carabiner yet"}`);
        // alone, this console's changes go to the helper, carried into its clock
        useClockLink({
          anyDomain: true,
          send: m => {
            if (m.type === "livecode_timeline_set") {
              const ms = fromGrid(Number(m.ms));
              if (ms != null) send({ ...m, ms });
            } else send(m);
          },
        });
        const ping = () => send({ type: "time_ping", t0: performance.now() });
        ping(); setTimeout(ping, 150); setTimeout(ping, 300);
        pingTimer = setInterval(ping, 2000);
        setTimeout(ask, 400);            // once the offset has a sample or three
      } else if (d?.type === "time_pong") {
        offset.sample(Number(d.t0), performance.now(), Number(d.serverNow));
      } else if (d?.type === "livecode_timeline") {
        linkBeat(d);
      }
    });
    me.addEventListener("close", () => {
      if (ws !== me) return;
      ws = null;
      clearInterval(pingTimer);
      useClockLink(null);
      if (stopped) return;
      if (!saidMissing) {
        log(`clock: no Link helper at ${address} -- start it (node olive-link.mjs) and Carabiner; trying again`, "warn");
        saidMissing = true;
      }
      timer = setTimeout(connect, retryMs);
      retryMs = Math.min(retryMs * 2, RETRY_MOST_MS);
    });
  }

  // the grid's clock changes when a room opens (the relay's) or closes (this
  // machine's): Link's beat asked for again, to be carried into the new one
  const onRoomOpen = () => {
    let tries = 0;
    const wait = setInterval(() => {
      if (gridNow().domain === "server" || ++tries > 50) { clearInterval(wait); ask(); }
    }, 100);
  };
  const onRoomClosed = () => ask();
  window.addEventListener("oscilla:room-open", onRoomOpen);
  window.addEventListener("oscilla:room-closed", onRoomClosed);

  connect();
  current = {
    address,
    stop() {
      stopped = true;
      clearTimeout(timer); clearInterval(pingTimer);
      unwatch();
      window.removeEventListener("oscilla:room-open", onRoomOpen);
      window.removeEventListener("oscilla:room-closed", onRoomClosed);
      useClockLink(null);
      ws?.close();
      current = null;
      try { localStorage.removeItem(KEY); } catch { /* private mode */ }
    },
  };
  return current;
}

/** Off: the helper let go, and not joined again on a reload. */
export function stopHelperLink() { current?.stop(); }

export const helperLinkOn = () => !!current;

/** On a page load: joined again if this browser had it on. */
export function resumeHelperLink(opts) {
  let address = null;
  try { address = localStorage.getItem(KEY); } catch { /* private mode */ }
  if (address) startHelperLink({ address, ...opts });
}
